import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { emitToast } from "../components/Toast";
import s from "./Reviewer.module.css";

const VERDICT_LABEL: Record<string, string> = {
  forte: "Forte", parcial: "Parcial", fraco: "Fraco", sem_resposta: "Sem resposta",
};

export function CandidateReview({ email }: { email: string }) {
  const { sessionId = "" } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [dims, setDims] = useState<any[]>([]);
  const [pens, setPens] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: session } = await supabase.from("rec_candidate_sessions").select("*").eq("id", sessionId).maybeSingle();
    if (!session) { setLoading(false); return; }
    const [{ data: assessment }, { data: score }, { data: questions }, { data: answers }, { data: audios }, { data: files }, { data: tele }] = await Promise.all([
      supabase.from("rec_assessments").select("*").eq("id", session.assessment_id).single(),
      supabase.from("rec_scores").select("*").eq("session_id", sessionId).maybeSingle(),
      supabase.from("rec_questions").select("id, order_index, type, prompt, section").eq("assessment_id", session.assessment_id).order("order_index"),
      supabase.from("rec_answers").select("*").eq("session_id", sessionId),
      supabase.from("rec_audio_recordings").select("*").eq("session_id", sessionId),
      supabase.from("rec_uploaded_files").select("*").eq("session_id", sessionId),
      supabase.from("rec_telemetry_events").select("*").eq("session_id", sessionId).order("occurred_at"),
    ]);
    let scoreDims: any[] = [], penalties: any[] = [], flags: any[] = [], analyses: any[] = [];
    if (score) {
      const [d, p, f, an] = await Promise.all([
        supabase.from("rec_score_dimensions").select("*").eq("score_id", score.id),
        supabase.from("rec_penalty_flags").select("*").eq("score_id", score.id),
        supabase.from("rec_anti_cheat_flags").select("*").eq("session_id", sessionId),
        supabase.from("rec_answer_analyses").select("*").eq("score_id", score.id),
      ]);
      scoreDims = d.data || []; penalties = p.data || []; flags = f.data || []; analyses = an.data || [];
    }
    // signed urls
    const audiosS = await Promise.all((audios || []).map(async (a) => ({ ...a, url: (await supabase.storage.from("rec-audio").createSignedUrl(a.storage_path, 3600)).data?.signedUrl })));
    const filesS = await Promise.all((files || []).map(async (f) => ({ ...f, url: (await supabase.storage.from("rec-uploads").createSignedUrl(f.storage_path, 3600)).data?.signedUrl })));
    const ansByQ: Record<string, any> = {}; for (const a of answers || []) ansByQ[a.question_id] = a;
    const audByQ: Record<string, any> = {}; for (const a of audiosS) audByQ[a.question_id] = a;
    const filByQ: Record<string, any> = {}; for (const f of filesS) filByQ[f.question_id] = f;
    const anaByQ: Record<string, any> = {}; for (const a of analyses) anaByQ[a.question_id] = a;

    setData({ session, assessment, score, questions: questions || [], ansByQ, audByQ, filByQ, anaByQ, flags, tele: tele || [] });
    const rubricPen = (assessment?.rubric?.penalties || []) as any[];
    setDims(scoreDims.map((d) => ({ ...d })));
    setPens(penalties.map((p) => ({ ...p, mag: (rubricPen.find((r) => r.key === p.penalty_key)?.points) ?? p.points })));
    setLoading(false);
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className={s.page}><p className="muted">Carregando candidato…</p></div>;
  if (!data) return <div className={s.page}><div className={s.empty}>Sessão não encontrada.</div></div>;

  const base = dims.reduce((acc, d) => acc + (Number(d.final_points) || 0), 0);
  const penTotal = pens.reduce((acc, p) => acc + (p.triggered ? Number(p.mag) : 0), 0);
  const adjusted = base + penTotal;

  async function saveFinal() {
    if (!data.score) { emitToast("warning", "Ainda sem correção draft para finalizar."); return; }
    setSaving(true);
    try {
      const adjustments: any[] = [];
      for (const d of dims) {
        await supabase.from("rec_score_dimensions").update({ final_points: d.final_points }).eq("id", d.id);
        if (Number(d.final_points) !== Number(d.ai_points)) adjustments.push({ score_id: data.score.id, dimension_key: d.dimension_key, field: "final_points", old_value: String(d.ai_points), new_value: String(d.final_points), by: email });
      }
      for (const p of pens) {
        await supabase.from("rec_penalty_flags").update({ triggered: p.triggered, points: p.triggered ? p.mag : 0, source: "reviewer" }).eq("id", p.id);
      }
      await supabase.from("rec_scores").update({ base_score: base, penalty_total: penTotal, adjusted_score: adjusted, status: "final", graded_by: email, graded_at: new Date().toISOString() }).eq("id", data.score.id);
      await supabase.from("rec_candidate_sessions").update({ status: "reviewed" }).eq("id", sessionId);
      if (adjustments.length) await supabase.from("rec_score_adjustments").insert(adjustments);
      emitToast("success", "Nota finalizada.");
      load();
    } catch (e) { emitToast("error", (e as Error).message); }
    setSaving(false);
  }

  const ident = data.session.meta?.identification;

  return (
    <div className={s.page}>
      <div className={s.h}>
        <div>
          <button className="btn btn-ghost" style={{ padding: "4px 12px", marginBottom: 8 }} onClick={() => nav(-1)}>← Voltar</button>
          <h2>{data.session.candidate_name || "Candidato"}</h2>
          <p className="dim">{data.assessment.title} · {ident?.email} · {ident?.phone} · {ident?.city} · pretensão {ident?.pretensao}</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className={`${s.score} ${s.big}`}>{adjusted}</div>
          <div className="dim">base {base} · penal {penTotal} {data.score?.status === "final" ? "· final ✓" : "· draft"}</div>
        </div>
      </div>

      <div className={s.grid2}>
        {/* esquerda: notas + penalidades + flags */}
        <div className="card" style={{ padding: "var(--s-4)", display: "flex", flexDirection: "column", gap: "var(--s-4)" }}>
          <div>
            <h3>Notas por dimensão</h3>
            {dims.length === 0 ? <p className="dim">Sem correção draft ainda.</p> : dims.map((d, i) => (
              <div key={d.id} className={s.dim}>
                <span className={s.label}>{d.label}{Number(d.confidence) < 0.6 && d.confidence != null && <span className={s.lowconf}> · conf {Math.round(d.confidence * 100)}% (revisar)</span>}</span>
                <input type="number" min={0} max={d.max_points} value={d.final_points ?? 0}
                  onChange={(e) => { const v = Math.max(0, Math.min(d.max_points, Number(e.target.value))); const next = [...dims]; next[i] = { ...d, final_points: v }; setDims(next); }} />
                <span className={s.max}>/{d.max_points}</span>
              </div>
            ))}
          </div>

          <div>
            <h3>Penalidades</h3>
            {pens.map((p, i) => (
              <div key={p.id} className={s.pen}>
                <div className={`${s.toggle} ${p.triggered ? s.on : ""}`} onClick={() => { const next = [...pens]; next[i] = { ...p, triggered: !p.triggered }; setPens(next); }}><i /></div>
                <span>{p.label}</span>
                <span className={s.pts}>{p.triggered ? p.mag : 0}</span>
              </div>
            ))}
          </div>

          <div>
            <h3>Flags anti-cola ({data.flags.length})</h3>
            {data.flags.length === 0 ? <p className="dim">Nenhuma.</p> : data.flags.map((f: any) => (
              <div key={f.id} className={`${s.flag} ${s[f.severity] || ""}`}>
                <span className={s.sig}>{f.signal}</span> <span className="dim">· {f.severity}</span>
                <div className={s.evi}>{f.evidence}</div>
              </div>
            ))}
          </div>

          <button className="btn btn-primary btn-block" disabled={saving || !data.score} onClick={saveFinal}>{saving ? "Salvando…" : "Salvar como final"}</button>
          {data.score && <p className="dim" style={{ fontSize: 12 }}>Resumo IA: {data.score.summary}</p>}
          {data.score?.red_flags && <p className={s.evi} style={{ color: "var(--warning)" }}>Red flags: {data.score.red_flags}</p>}
        </div>

        {/* direita: respostas / transcrições / arquivos / telemetria */}
        <div>
          {data.questions.map((q: any) => {
            const a = data.ansByQ[q.id]; const au = data.audByQ[q.id]; const fi = data.filByQ[q.id]; const an = data.anaByQ[q.id];
            return (
              <div key={q.id} className={s.qa}>
                <h4>Q{q.order_index} · {q.type} {q.section ? `· ${q.section}` : ""}</h4>
                {q.prompt && <div className={s.prompt}>{q.prompt}</div>}
                <div className={s.ansLabel}>Resposta do candidato</div>
                {a?.answer_text && <div className="ans" style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{a.answer_text}</div>}
                {a?.answer_choice && <div className="ans"><span className="badge badge-info">escolha: {JSON.stringify(a.answer_choice)}</span></div>}
                {au && (<div>
                  {au.url && <audio controls src={au.url} />}
                  <div className={s.transcript}>{au.transcript_meta?.silencio ? "[áudio sem fala / mic-fail]" : (au.transcript || "(sem transcrição)")}{au.transcript_meta && <span className="dim"> — disfluências: {au.transcript_meta.fillers ?? "?"}{au.transcript_meta.scripted_suspect ? " · suspeita de leitura" : ""}</span>}</div>
                </div>)}
                {fi && (<div style={{ marginTop: 6 }}>
                  <a href={fi.url} target="_blank" rel="noreferrer" className="badge badge-info">{fi.file_name || "arquivo"} ↗</a>
                  {fi.analysis && <div className={s.evi}>correção {pct(fi.analysis.correctness)} · originalidade {pct(fi.analysis.originality)} · IA {pct(fi.analysis.ai_likelihood)} — {fi.analysis.evidence}</div>}
                </div>)}
                {!a?.answer_text && !a?.answer_choice && !au && !fi && <span className="dim">— sem resposta —</span>}

                {an && (an.analysis || an.verdict) && (
                  <div className={`${s.aiBox} ${s["ai_" + (an.verdict || "")] || ""}`}>
                    <div className={s.aiHead}>
                      <span className={s.aiTag}>Análise da IA</span>
                      {an.verdict && <span className={`${s.verdict} ${s["v_" + an.verdict] || ""}`}>{VERDICT_LABEL[an.verdict] || an.verdict}</span>}
                      {an.confidence != null && Number(an.confidence) < 0.6 && <span className={s.lowconf}>· conf {Math.round(an.confidence * 100)}% (revisar)</span>}
                    </div>
                    {an.analysis && <div className={s.aiText}>{an.analysis}</div>}
                    {an.evidence && <div className={s.aiEvi}>“{an.evidence}”</div>}
                  </div>
                )}
              </div>
            );
          })}
          {data.tele.length > 0 && (
            <div className={s.qa}>
              <h4>Telemetria ({data.tele.length})</h4>
              <div className={s.evi}>{data.tele.map((t: any) => `${t.event_type}`).reduce((acc: Record<string, number>, x: string) => (acc[x] = (acc[x] || 0) + 1, acc), {}) && Object.entries(data.tele.reduce((acc: Record<string, number>, t: any) => (acc[t.event_type] = (acc[t.event_type] || 0) + 1, acc), {})).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function pct(n: number) { return `${Math.round((Number(n) || 0) * 100)}%`; }
